import { useLanguage } from '../context/LanguageContext';
import { Check, ArrowRight, Sparkles, Diamond, Crown, Users } from 'lucide-react';

export const ServicesSection = () => {
  const { t } = useLanguage();

  const mainPacks = [
    {
      key: 'bronze',
      icon: Sparkles,
      accent: '#cd7f32',
      popular: false
    },
    {
      key: 'diamond',
      icon: Diamond,
      accent: '#b9f2ff',
      popular: true
    },
    {
      key: 'gold',
      icon: Crown,
      accent: '#ffd700',
      popular: false
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-padding bg-white" data-testid="services-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="font-serif text-4xl md:text-5xl font-bold text-[#004d40] mb-4"
            data-testid="services-title"
          >
            {t('services.title')}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Main Packs - Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {mainPacks.map((pack, index) => {
            const Icon = pack.icon;
            const features = t(`services.${pack.key}.features`);
            
            return (
              <div
                key={pack.key}
                className={`service-card p-8 card-hover relative ${
                  pack.popular ? 'md:-mt-4 md:mb-4 ring-2 ring-[#4caf50]' : ''
                }`}
                data-testid={`service-card-${pack.key}`}
              >
                {pack.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4caf50] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                    Populaire
                  </div>
                )}

                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${pack.accent}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: pack.accent === '#b9f2ff' ? '#00bcd4' : pack.accent }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#004d40]">
                      {t(`services.${pack.key}.name`)}
                    </h3>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      {t(`services.${pack.key}.subtitle`)}
                    </p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <span className="price-tag">
                    {t(`services.${pack.key}.price`)}
                  </span>
                </div>

                {/* Description */}
                <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                  {t(`services.${pack.key}.desc`)}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {Array.isArray(features) && features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-5 h-5 text-[#4caf50] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={scrollToContact}
                  className={`w-full py-3 rounded-full font-medium text-sm inline-flex items-center justify-center gap-2 group transition-all duration-300 ${
                    pack.popular
                      ? 'bg-[#4caf50] text-white hover:bg-[#388e3c]'
                      : 'border-2 border-[#004d40] text-[#004d40] hover:bg-[#004d40] hover:text-white'
                  }`}
                  data-testid={`service-cta-${pack.key}`}
                >
                  {t('services.cta')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Strategic Circles - Full Width Card */}
        <div 
          className="bg-[#004d40] rounded-2xl p-8 md:p-12 text-white"
          data-testid="strategic-circles-card"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4caf50]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    {t('services.strategic.name')}
                  </h3>
                  <p className="text-xs text-white/70 uppercase tracking-wide">
                    {t('services.strategic.subtitle')}
                  </p>
                </div>
              </div>

              <p className="text-white/80 mb-6 leading-relaxed">
                {t('services.strategic.desc')}
              </p>

              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold text-[#4caf50]">
                  {t('services.strategic.price')}
                </span>
              </div>
            </div>

            <div>
              <ul className="space-y-4">
                {Array.isArray(t('services.strategic.features')) && t('services.strategic.features').map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#4caf50] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white/90">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={scrollToContact}
                className="mt-8 btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 group"
                data-testid="strategic-cta"
              >
                {t('services.cta')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Specific Services */}
        <div className="mt-16">
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#004d40] mb-8 text-center">
            {t('specificServices.title')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['diagnostic', 'branding', 'brand', 'business'].map((service) => (
              <div
                key={service}
                className="bg-[#f8fafc] rounded-xl p-6 border border-slate-100 hover:border-[#4caf50] transition-colors duration-300"
                data-testid={`specific-service-${service}`}
              >
                <h4 className="font-bold text-[#004d40] mb-2">
                  {t(`specificServices.${service}.title`)}
                </h4>
                <p className="text-[#4caf50] font-semibold mb-3">
                  {t('services.from')} {t(`specificServices.${service}.price`)}
                </p>
                <p className="text-slate-600 text-sm">
                  {t(`specificServices.${service}.result`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
